import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DynamicSearchField - text input with a server-driven suggestions dropdown
 *
 * Looks and behaves like a regular text field. When the parent passes a non-empty
 * `suggestions` array the dropdown appears below the input. The parent is
 * responsible for fetching suggestions (e.g. debounced API calls) - this
 * component does no client-side filtering.
 *
 * Selecting an item calls `onSelect` rather than `onChange`, so the parent can
 * do additional work on selection (e.g. decompose an address into sibling fields).
 *
 * No ghost completion, no client-side filtering, no "Use…" create row.
 */
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import * as Popover from '@radix-ui/react-popover';
import * as React from 'react';
export function DynamicSearchField({ value, onChange, onSelect, suggestions, placeholder, disabled = false, loading = false, size = 'comfortable', id, name, }) {
    const inputId = id || React.useId();
    const [open, setOpen] = React.useState(false);
    const inputRef = React.useRef(null);
    const wrapRef = React.useRef(null);
    const [contentWidth, setContentWidth] = React.useState();
    // Open when there are suggestions; close when the list becomes empty or field is disabled
    React.useEffect(() => {
        if (suggestions.length > 0 && !disabled) {
            setContentWidth(wrapRef.current?.offsetWidth);
            setOpen(true);
        }
        else {
            setOpen(false);
        }
    }, [suggestions, disabled]);
    const handleSelect = (option) => {
        setOpen(false);
        onSelect(option);
        // Return focus to the input after selection
        requestAnimationFrame(() => inputRef.current?.focus());
    };
    return (_jsxs(Popover.Root, { open: open, onOpenChange: (next) => {
            if (!next)
                setOpen(false);
        }, children: [_jsx(Popover.Anchor, { asChild: true, children: _jsxs("div", { ref: wrapRef, className: "dynamic-search-field__wrap", children: [_jsx("input", { ref: inputRef, id: inputId, name: name, type: "text", className: [
                                'text-field__input',
                                'dynamic-search-field__input',
                                size === 'comfortable' ? 'text-field__input--comfortable' : '',
                            ]
                                .filter(Boolean)
                                .join(' '), value: value, placeholder: placeholder, disabled: disabled, autoComplete: "off", spellCheck: false, onChange: (e) => onChange(e.target.value), onBlur: () => {
                                // Small delay so a click on a dropdown item registers first
                                setTimeout(() => setOpen(false), 150);
                            }, onFocus: () => {
                                if (suggestions.length > 0 && !disabled)
                                    setOpen(true);
                            }, onKeyDown: (e) => {
                                if (e.key === 'Escape')
                                    setOpen(false);
                            } }), _jsx("span", { className: "dynamic-search-field__icon", "aria-hidden": "true", children: loading ? (_jsx("span", { className: "dynamic-search-field__spinner", "aria-hidden": "true" })) : (_jsx(MagnifyingGlassIcon, { width: 14, height: 14 })) })] }) }), _jsx(Popover.Content, { className: "dynamic-search-field__content", align: "start", sideOffset: 4, style: { width: contentWidth }, 
                // Prevent the popover from stealing focus from the input
                onOpenAutoFocus: (e) => e.preventDefault(), 
                // Don't close when the user clicks inside the input wrapper
                onInteractOutside: (e) => {
                    if (e.target?.closest?.('.dynamic-search-field__wrap')) {
                        e.preventDefault();
                    }
                }, children: suggestions.map((option) => (_jsx("div", { className: "dynamic-search-field__item", onMouseDown: (e) => {
                        // Prevent input blur before the click fires
                        e.preventDefault();
                    }, onClick: () => handleSelect(option), children: option }, option))) })] }));
}
//# sourceMappingURL=DynamicSearchField.js.map